import { Button, message, Modal } from "antd";
import "./style.scss";
import { usePostData } from "../../../Network/usePostData";
import { useState } from "react";
import OTPInput from "react-otp-input";
import { handleAction } from "../../../utils/requestStatus";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Props = {
  onCloseModal: () => void;
  onResend: () => void;
  refetch?: () => void;
  phoneNum?: string;
  paymentMethod?: string;
  otpData: {
    serviceProviderId: number;
    customerId?: string;
  };
};

export const OtpConfirmation = ({
  phoneNum,
  otpData,
  paymentMethod,
  onCloseModal,
  onResend,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const { requestId } = useParams();

  const [otpStatus, setOtpStatus] = useState<"success" | "error" | undefined>();
  const [otpValue, setOtpValue] = useState<string>();

  const { mutateAsync: verifyOtp, isPending: loadingOtp } = usePostData({
    url: "customer/verify-otp",
    options: {
      onSuccess: async (data: any) => {
        setOtpStatus(!!data?.data?.isValid ? "success" : "error");
        if (data.data.isValid) {
          if (paymentMethod !== "cash")
            await handleAction(
              `/change-payment-method`,
              {
                serviceRequestId: requestId,
                paymentMethod: "cash",
              },
              refetch?.(),
              true
            );

          handleAction(`/${requestId}/complete`, undefined, refetch?.());
        }
      },
      onError: (error) => {
        setOtpStatus("error");
        message.error((error?.response?.data as any)?.message);
      },
    },
  });

  return (
    <Modal className="delete-provider-modal otp" open closable={false}>
      <h2>{t("otpConfirmation.verifyTitle")}</h2>
      <p>
        {t("otpConfirmation.enterMessage")} {phoneNum}
      </p>
      <div className="provider-details-details">
        <OTPInput
          value={otpValue}
          onChange={setOtpValue}
          numInputs={6}
          renderSeparator={<span>-</span>}
          renderInput={(props) => <input {...props} />}
        />
      </div>

      {otpStatus === "error" && (
        <p
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="33"
            height="32"
            viewBox="0 0 33 32"
            fill="none"
          >
            <path
              d="M16.5 0C7.668 0 0.5 7.168 0.5 16C0.5 24.832 7.668 32 16.5 32C25.332 32 32.5 24.832 32.5 16C32.5 7.168 25.332 0 16.5 0ZM16.5 24C15.62 24 14.9 23.28 14.9 22.4V16C14.9 15.12 15.62 14.4 16.5 14.4C17.38 14.4 18.1 15.12 18.1 16V22.4C18.1 23.28 17.38 24 16.5 24ZM18.1 11.2H14.9V8H18.1V11.2Z"
              fill="#FF0000"
            />
          </svg>
          {t("otpConfirmation.invalidCode")}
        </p>
      )}

      {otpStatus === "success" && (
        <p
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="33"
            height="32"
            viewBox="0 0 33 32"
            fill="none"
          >
            <path
              d="M16.5 0C13.3355 0 10.2421 0.938383 7.61088 2.69649C4.9797 4.45459 2.92894 6.95345 1.71793 9.87706C0.506932 12.8007 0.190079 16.0177 0.807443 19.1214C1.42481 22.2251 2.94866 25.0761 5.1863 27.3137C7.42394 29.5513 10.2749 31.0752 13.3786 31.6926C16.4823 32.3099 19.6993 31.9931 22.6229 30.7821C25.5466 29.5711 28.0454 27.5203 29.8035 24.8891C31.5616 22.2579 32.5 19.1645 32.5 16C32.5 11.7565 30.8143 7.68687 27.8137 4.68629C24.8131 1.68571 20.7435 0 16.5 0ZM26.95 10.63L13.81 23.76L6.05001 16C5.78479 15.7348 5.63579 15.3751 5.63579 15C5.63579 14.6249 5.78479 14.2652 6.05001 14C6.31522 13.7348 6.67493 13.5858 7.05001 13.5858C7.42508 13.5858 7.78479 13.7348 8.05001 14L13.83 19.78L24.97 8.65C25.1013 8.51868 25.2572 8.41451 25.4288 8.34344C25.6004 8.27236 25.7843 8.23578 25.97 8.23578C26.1557 8.23578 26.3396 8.27236 26.5112 8.34344C26.6828 8.41451 26.8387 8.51868 26.97 8.65C27.1013 8.78132 27.2055 8.93722 27.2766 9.1088C27.3476 9.28038 27.3842 9.46428 27.3842 9.65C27.3842 9.83571 27.3476 10.0196 27.2766 10.1912C27.2055 10.3628 27.1013 10.5187 26.97 10.65L26.95 10.63Z"
              fill="#8BC34A"
            />
          </svg>
          {t("otpConfirmation.codeVerified")}
        </p>
      )}

      <div className="modal-action">
        {!otpStatus && (
          <Button
            loading={loadingOtp}
            onClick={() => verifyOtp({ ...otpData, otp: otpValue })}
            type="primary"
          >
            {t("otpConfirmation.verify")}
          </Button>
        )}
        {otpStatus === "error" && (
          <Button
            loading={loadingOtp}
            onClick={() => verifyOtp({ ...otpData, otp: otpValue })}
            type="primary"
          >
            {t("otpConfirmation.retry")}
          </Button>
        )}
        {(!otpStatus || otpStatus !== "success") && (
          <Button
            loading={loadingOtp}
            onClick={() => {
              setOtpValue("");
              onResend();
            }}
            type="default"
          >
            {t("otpConfirmation.resend")}
          </Button>
        )}
        <Button onClick={onCloseModal} type="default">
          {otpStatus === "success"
            ? t("otpConfirmation.close")
            : t("otpConfirmation.cancel")}
        </Button>
      </div>
    </Modal>
  );
};
